import React, {useState} from 'react';
import classNames from 'classnames';
import {PDFIcon, UDownloadIcon} from 'components';
import {useBreakpoint} from 'hooks';
import {Receipt} from 'interfaces';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {ReceiptPdf} from './ReceiptPdf';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr; /* Five equal columns */
  gap: 1rem; /* Space between columns */

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr; /* Example of a different layout on wider screens */
  }
`;

export interface ReceiptsListProps {
  receipts: Receipt[];
}

export function ReceiptsList(props: ReceiptsListProps): React.ReactElement {
  const {t} = useTranslation();
  const {receipts} = props;

  return (
    <div>
      <GridContainer className="px-4 py-4">
        {['Date', 'Fee', 'Amount', '', ''].map((columnName, index) => (
          <div
            key={`receipt-header-${index}`}
            className={classNames('font-bold text-gray-700 uppercase text-xs', {
              'hidden md:block': index === 1,
              // 'text-right': index === 2,
            })}
          >
            {columnName.length
              ? t(`receipts.columnName.${columnName}`, columnName)
              : ''}
          </div>
        ))}
      </GridContainer>
      <div>
        {receipts.map((item, index) => (
          <ReceiptItem
            key={`receipt-item-${index}`}
            item={item}
            showBackground={false}
          />
        ))}
      </div>
    </div>
  );
}

export interface ReceiptItemProps {
  item: Receipt;
  showBackground: boolean;
}

export function ReceiptItem({
  item,
  showBackground,
}: ReceiptItemProps): React.ReactElement {
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [downloadPdf, setDownloadPdf] = useState<boolean>(false);

  const {t} = useTranslation();
  const {isNotMd} = useBreakpoint('md');

  return (
    <GridContainer
      className={classNames('items-center px-4 py-5 border-t border-gray-100', {
        'bg-gray-50 bg-opacity-90 hover:bg-opacity-100': showBackground,
      })}
    >
      <div>
        {t('receipts.date', {
          date: new Date(item.date),
          defaultValue: '{{ date, LLL dd, yyyy }}',
        })}
      </div>
      <div className="hidden md:block">{item.feePercentage}%</div>
      <div className="w-full flex flex-col space-y-2 md:flex-row text-left">
        <div className="md:w-full text-left">${item.totalAmount}</div>
        <div className="text-sm text-gray-600 md:hidden">
          {t('receipts.feePercentage', {
            feePercentage: item.feePercentage,
            defaultValue: `${item.feePercentage}% fee`,
          })}
        </div>
      </div>
      <div className="w-full group hidden md:block">
        <button
          className="inline-flex items-center space-x-1 text-sm text-blue-600 group-hover:text-blue-500 transition-all duration-300 group-hover:underline font-semibold"
          onClick={() => {
            setDownloadPdf(false);
            setShowPdf(true);
          }}
        >
          <PDFIcon width={17} height={17} strokeColor="inherit" />
          <span>
            {isNotMd
              ? t('receipts.viewReceiptButtonLabel', 'View Receipt').split(
                  ' ',
                )[0]
              : t('receipts.viewReceiptButtonLabel', 'View Receipt')}
          </span>
        </button>
      </div>
      <div className="w-full text-right group">
        <button
          className="inline-flex items-center space-x-1 text-sm text-blue-600 group-hover:text-blue-500 transition-all duration-300 group-hover:underline font-semibold"
          onClick={() => {
            setDownloadPdf(true);
            setShowPdf(true);
          }}
        >
          <UDownloadIcon
            width={20}
            height={20}
            strokeColor="inherit"
            title="Download Receipt"
          />
          <span>
            {isNotMd
              ? t(
                  'receipts.downloadReceiptButtonLabel',
                  'Download Receipt',
                ).split(' ')[0]
              : t('receipts.downloadReceiptButtonLabel', 'Download Receipt')}
          </span>
        </button>
      </div>
      {showPdf ? (
        <ReceiptPdf
          receipt={item}
          download={downloadPdf}
          onClose={() => {
            setShowPdf(false);
            setDownloadPdf(false);
          }}
        />
      ) : null}
    </GridContainer>
  );
}
